import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from "react-router-dom";

// Material-UI imports
import { useTheme } from '@mui/material/styles';
import { AccessTime, ArrowForward, DirectionsWalk, Place, Room } from '@mui/icons-material';
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import FormControl from '@mui/material/FormControl';
import { IconButton } from "@mui/material";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import Typography from "@mui/material/Typography";

// Third-party library imports
import { Flex } from "reflexbox";

// Local component imports
import CourseDetails from "./CourseDetails";
import PageHeader from "../gamePlayComponents/PageHeader";
import SwipeableImage from "../common/SwipeableImage";

// Utility/helper imports
import { writeCourseSelection } from "../../helpers/dbFunctions";
import { appBackgroundColor, PRIMARYCOLOR } from "../helpers/ThemeSettings";
import * as route from "../helpers/routes";
import { ToSummary } from "../helpers/routes";
import { CITIES, courses, experiences } from "../courseContent/courseObjects";
import { isDev, isProd } from "../helpers/misc";
import { usePlatformInsets } from "../helpers/usePlatformInsets";

// Helper function: computes distance in kilometers between two coordinates
const haversineDistance = (coords1, coords2) => {
    const toRad = (value) => (value * Math.PI) / 180;
    const lat1 = coords1.lat;
    const lon1 = coords1.lon;
    const lat2 = coords2.lat;
    const lon2 = coords2.lon;
    const R = 6371; // Earth's radius in kilometers
    const dLat = toRad(lat2 - lat1);
    const dLon = toRad(lon2 - lon1);
    const a =
        Math.sin(dLat / 2) ** 2 +
        Math.cos(toRad(lat1)) *
        Math.cos(toRad(lat2)) *
        Math.sin(dLon / 2) ** 2;
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
};

// Utility function to add a timeout to a fetch request
const fetchWithTimeout = (url, options = {}, timeout = 4000) => {
    return new Promise((resolve, reject) => {
        const timer = setTimeout(() => reject(new Error('Request timed out')), timeout);
        fetch(url, options)
            .then(response => {
                clearTimeout(timer);
                resolve(response);
            })
            .catch(err => {
                clearTimeout(timer);
                reject(err);
            });
    });
};

const isExperienceAvailable = (experience, referenceDate) => {
    if (!experience?.availabilityWindow) {
        return true;
    }

    const now = referenceDate || new Date();
    const {start, end} = experience.availabilityWindow;
    const startDate = new Date(start);
    const endDate = new Date(end);

    return now >= startDate && now <= endDate;
};

export const COURSE_VIEW_TYPES = {
    CASUAL: 'casual',
    HOST: 'host',
    NON_HOST: 'nonHost',
};

const TENTATIVE_SELECTED_COURSE_KEY = 'tentativeSelectedCourse';

const getCourseViewConfig = (courseViewType, deps) => {
    const { navigate, dispatch, currentTeamId } = deps;

    switch (courseViewType) {
        case COURSE_VIEW_TYPES.HOST:
            return {
                ctaLabel: 'Select',
                onCourseSelect: (courseName) => {
                    writeCourseSelection(currentTeamId, courseName);
                    navigate(route.LOBBY + '?step=2');
                },
            };
        case COURSE_VIEW_TYPES.NON_HOST:
            return {
                ctaLabel: 'Back to lobby',
                onCourseSelect: () => {
                    // View-only: non-hosts do not persist course selection.
                    navigate(route.LOBBY);
                },
            };
        case COURSE_VIEW_TYPES.CASUAL:
        default:
            return {
                ctaLabel: 'Play this course',
                onCourseSelect: (courseName) => {
                    if (currentTeamId) {
                        writeCourseSelection(currentTeamId, courseName);
                    } else {
                        sessionStorage.setItem(TENTATIVE_SELECTED_COURSE_KEY, courseName);
                    }
                    navigate(route.PROFILE_SETUP);
                },
            };
    }
};

const CourseSelectorScreen = (props) => {
    const { courseViewType = COURSE_VIEW_TYPES.CASUAL } = props;
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const theme = useTheme();
    const { bottomInset } = usePlatformInsets();

    const cityQueryParam = new URLSearchParams(location.search).get("city");
    const courseQueryParam = new URLSearchParams(location.search).get("course");

    //array of cities values
    const cityOptions = useMemo(() => Object.values(CITIES), []);

    const [city, setCity] = useState(cityQueryParam || CITIES.washingtonDC);
    const currentTeamId = useSelector((state) => state.team?.currentTeamId);
    const [loading, setLoading] = useState(true);

    const courseViewConfig = useMemo(
        () => getCourseViewConfig(courseViewType, { navigate, dispatch, currentTeamId }),
        [courseViewType, navigate, dispatch, currentTeamId]
    );

    // this useEffect checks the user location and sets the default city.
    useEffect(() => {
        const CACHE_KEY = 'userLocation';
        const CACHE_EXPIRATION = 7 * 24 * 60 * 60 * 1000; // 7 days

        // Helper function to process location data
        const processLocation = (data) => {
            const userCoords = {lat: data.latitude, lon: data.longitude};
            const dcCoords = {lat: 38.9072, lon: -77.0369};
            const nycCoords = {lat: 40.7128, lon: -74.0060};

            const distanceToDC = haversineDistance(userCoords, dcCoords);
            const distanceToNYC = haversineDistance(userCoords, nycCoords);

            const defaultCity =
                Math.min(distanceToDC, distanceToNYC) > 320 // if user is further than 200 miles, default to DC
                    ? CITIES.washingtonDC
                    : distanceToDC < distanceToNYC
                        ? CITIES.washingtonDC
                        : CITIES.newYorkCity;

            setCity(defaultCity);
            setLoading(false);
        };

        const fetchLocation = async () => {
            if (cityQueryParam) {
                setLoading(false);
                return;
            }
            try {
                const cached = localStorage.getItem(CACHE_KEY);

                if (cached) {
                    const {location, timestamp} = JSON.parse(cached);
                    if (Date.now() - timestamp < CACHE_EXPIRATION) {
                        processLocation(location);
                        return;
                    }
                }

                // Avoid burning the free IP API quota during local/dev usage
                if (!isProd) {
                    setLoading(false);
                    return;
                }

                const response = await fetchWithTimeout('https://ipapi.co/json/', {}, 4000);
                const data = await response.json();
                localStorage.setItem(
                    CACHE_KEY,
                    JSON.stringify({location: data, timestamp: Date.now()})
                );
                processLocation(data);
            } catch (err) {
                console.error('Error fetching geolocation:', err);
                setLoading(false);
            }
        };

        fetchLocation();
    }, []);


    const handleUpdateCourse = (courseName) => {
        if (currentTeamId) {
            writeCourseSelection(currentTeamId, courseName);
        }
    };

    const handleCourseSelection = (courseName) => {
        courseViewConfig.onCourseSelect(courseName);
    };

    const handleCourseSeeSummary = (courseName) => {
        handleUpdateCourse(courseName);
        navigate(ToSummary(courseName));
    };

    const handleCourseResume = (courseName, url) => {
        handleUpdateCourse(courseName);
        navigate(url);
    };

    const courseOptionsForSelectCity = useMemo(() => {
        const referenceDate = new Date();

        return Object.values(experiences)
            // Hide the QA-only test course in production
            .filter((experience) => isDev || experience.id !== 'testCourse')
            .filter((experience) => experience.city === city)
            .filter((experience) => isExperienceAvailable(experience, referenceDate))
            .map((experience) => ({
                ...experience,
                startOptions: experience.variantKeys
                    .map((courseKey) => courses[courseKey]?.startOption)
                    .filter(Boolean),
            }))
            .filter((experience) => experience.startOptions.length > 0);
    }, [city, experiences, courses]);

    // Resolve ?course= param: can be array index (from course list) or course key (from Lobby/currentCourseSelection)
    const resolvedCourseForParam = useMemo(() => {
        if (!courseQueryParam) return null;
        const asIndex = parseInt(courseQueryParam, 10);
        if (!Number.isNaN(asIndex) && courseOptionsForSelectCity[asIndex]) {
            return courseOptionsForSelectCity[asIndex];
        }
        if (courses[courseQueryParam]) {
            const experience = Object.values(experiences).find((exp) =>
                exp.variantKeys?.includes(courseQueryParam)
            );
            if (experience) {
                return {
                    ...experience,
                    startOptions: experience.variantKeys
                        .map((courseKey) => courses[courseKey]?.startOption)
                        .filter(Boolean),
                };
            }
        }
        return null;
    }, [courseQueryParam, courseOptionsForSelectCity]);

    const courseCards = courseOptionsForSelectCity
                        .map((el, key) => (
                            <Card
                                key={key}
                                style={{
                                    margin: '16px 0',
                                    overflow: 'hidden',
                                    position: 'relative',
                                }}
                                onClick={() => navigate(location.pathname + '?city=' + el.city + '&course=' + key)}
                            >
                                {/* Swipeable Image Component */}
                                <SwipeableImage
                                    images={el.images}
                                    height={200}
                                    showNav={true}
                                    showNavArrows={false}
                                    overlayNavOnImage={true} // Adjust as needed
                                />

                                {/* Chip Indicator */}
                                {el.chip && (
                                    <Chip
                                        label={el.chip}
                                        color="primary"
                                        style={{
                                            position: 'absolute',
                                            top: 12,
                                            left: 12,
                                            zIndex: 2,
                                            fontWeight: 'bold',
                                        }}
                                    />
                                )}

                                {/* name and quick stats */}
                                <Flex style={{margin: '8px 16px'}} justifyContent={'space-between'} alignItems={'center'}>
                                    <Flex flexDirection={'column'}>
                                        <Typography variant="subtitle1" style={{fontWeight: 'bold'}}>
                                            {el.name}
                                        </Typography>

                                        <Flex id={'course stats'} alignItems="center" mb={1}>
                                            <Box display="flex" alignItems="center" mr={2}>
                                                <AccessTime style={{fontSize: 16, marginRight: 4}}/>
                                                <Typography variant="caption">{el.time}</Typography>
                                            </Box>

                                            <Box display="flex" alignItems="center" mr={2}>
                                                <DirectionsWalk style={{fontSize: 16, marginRight: 4}}/>
                                                <Typography variant="caption">{el.distance}</Typography>
                                            </Box>

                                            <Box display="flex" alignItems="center">
                                                <Room style={{fontSize: 16, marginRight: 4}}/>
                                                <Typography variant="caption">{el.locationInCity}</Typography>
                                            </Box>

                                            <Box flexGrow={1}/>

                                        </Flex>

                                    </Flex>
                                    <IconButton
                                        size="small"
                                        color="primary"
                                        variant={'contained'}
                                        style={{
                                            backgroundColor: theme.palette.primary.main,
                                            color: theme.palette.primary.contrastText,
                                            width: 50,
                                            height: 50,
                                        }}
                                    >
                                        <ArrowForward/>
                                    </IconButton>
                                </Flex>
                            </Card>
                        )
    )

    return (
        <>
            {!courseQueryParam ? (
                <>
                    <PageHeader goBack title={courseViewType === COURSE_VIEW_TYPES.HOST ? 'Choose your course' : 'Courses:'}/>
                    {loading ? (
                        <Flex style={{
                            width: '100%',
                            height: '50%',
                            backgroundColor: appBackgroundColor,
                            justifyContent: 'center',
                            alignItems: 'center'
                        }}>
                            <CircularProgress size={70}/>
                        </Flex>
                    ) : (
                        <>
                            {/* <Typography variant={'h6'} style={{color: PRIMARYCOLOR, padding: '4px'}}>
                                {courseViewType === COURSE_VIEW_TYPES.HOST ? 'Choose your course' : 'Courses:'}
                            </Typography> */}
                            <Flex id={'city selector'} style={{alignItems: 'center'}}>
                                <Place style={{color: PRIMARYCOLOR}}/>
                                <FormControl variant="standard">
                                <Select
                                    style={{minWidth: '190px', paddingLeft: '4px', margin: '4px'}}
                                    onChange={(event) => setCity(event.target.value)}
                                    value={city}
                                >
                                    {cityOptions.map((el) => <MenuItem key={el} value={el}>{el}</MenuItem>)}
                                </Select>
                                </FormControl>
                            </Flex>
                            {courseCards}
                            <div style={{ height: `${bottomInset}px`, flexShrink: 0 }} />
                        </>
                    )}
                </>
            ) : resolvedCourseForParam ? (
                <CourseDetails
                    course={resolvedCourseForParam}
                    handleCourseSelection={handleCourseSelection}
                    handleCourseSeeSummary={handleCourseSeeSummary}
                    handleCourseResume={handleCourseResume}
                    ctaLabel={courseViewConfig.ctaLabel}
                    viewOnly={courseViewType === COURSE_VIEW_TYPES.NON_HOST}
                />
            ) : (
                <PageHeader goBack />
            )}
        </>
    );
};

export default CourseSelectorScreen;
